import { useMemo } from 'react'
import type { ShopI18nContextValue } from '../../../../i18n/provider/types'
import type { useCustomerAuthForm } from '../../../hooks/useCustomerAuthForm'
import type { AuthFlowState } from './AuthFlow.types'

type UseAuthViewModelInput = {
  state: AuthFlowState
  formState: ReturnType<typeof useCustomerAuthForm>
  t: ShopI18nContextValue['t']
}

type UseAuthViewModelOutput = {
  isSignup: boolean
  title: string
  subtitle: string
  trustItems: string[]
  trustHint: string
  currentSignupStepLabel: string
  currentSignupStepDescription: string
  normalizedTwoFactorCode: string
  twoFactorCodeError: string
  showTwoFactorCard: boolean
  showPasskeyButton: boolean
}

export const useAuthViewModel = ({ state, formState, t }: UseAuthViewModelInput): UseAuthViewModelOutput => {
  const isSignup = formState.isSignup
  const trustItems = [
    t('customer.auth.trust.payments'),
    t('customer.auth.trust.orders'),
    t('customer.auth.trust.support'),
  ]
  const trustHint = isSignup ? t('customer.auth.signupTrustHint') : t('customer.auth.loginTrustHint')
  const signupStepLabels = [
    t('customer.auth.signupStep.personal'),
    t('customer.auth.signupStep.contact'),
    t('customer.auth.signupStep.security'),
    t('customer.auth.signupStep.review'),
  ]
  const signupStepDescriptions = [
    t('customer.auth.signupSubtitle'),
    t('customer.auth.contactData'),
    t('customer.auth.securityData'),
    t('customer.auth.signupTrustHint'),
  ]

  const currentSignupStepLabel =
    signupStepLabels[Math.max(0, Math.min(signupStepLabels.length - 1, formState.signupStep - 1))] ?? ''
  const currentSignupStepDescription =
    signupStepDescriptions[Math.max(0, Math.min(signupStepDescriptions.length - 1, formState.signupStep - 1))] ?? ''

  const title = t(formState.titleKey)
  const subtitle = t(formState.subtitleKey)

  const normalizedTwoFactorCode = state.twoFactorCode.replace(/\s+/g, '').trim()
  const twoFactorCodeError =
    state.twoFactorTouched && normalizedTwoFactorCode.length < 6
      ? t('customer.auth.validation.requiredField')
      : ''

  const showTwoFactorCard = Boolean(state.twoFactorChallenge) && !isSignup
  const showPasskeyButton = Boolean(state.twoFactorChallenge?.passkeysAvailable)

  return useMemo(
    () => ({
      isSignup,
      title,
      subtitle,
      trustItems,
      trustHint,
      currentSignupStepLabel,
      currentSignupStepDescription,
      normalizedTwoFactorCode,
      twoFactorCodeError,
      showTwoFactorCard,
      showPasskeyButton,
    }),
    [
      currentSignupStepDescription,
      currentSignupStepLabel,
      isSignup,
      normalizedTwoFactorCode,
      showTwoFactorCard,
      subtitle,
      title,
      trustHint,
      trustItems,
      twoFactorCodeError,
      showPasskeyButton,
    ],
  )
}
